import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";

import {
  deleteQualityNcrPhoto,
  fetchQualityNcrPhotoBlob,
  fetchQualityNcrPhotos,
  qualityNcrPhotosQueryKey,
  uploadQualityNcrPhoto,
  type QualityNcrDevicePhoto,
} from "./qualityEventNcrApi";

/** Photos attached to an NCR. They do not appear on the customer RCA report. */
export function QualityNcrDevicePhotos({ ncrId, readOnly }: { ncrId: number; readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const photosQuery = useQuery({
    queryKey: qualityNcrPhotosQueryKey(ncrId),
    queryFn: () => fetchQualityNcrPhotos(ncrId),
  });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        await uploadQualityNcrPhoto(ncrId, file);
      }
    },
    onSuccess: async () => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: qualityNcrPhotosQueryKey(ncrId) });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Could not upload the photo.");
    },
  });
  const remove = useMutation({
    mutationFn: (photoId: number) => deleteQualityNcrPhoto(ncrId, photoId),
    onSuccess: async () => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: qualityNcrPhotosQueryKey(ncrId) });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Could not remove the photo.");
    },
  });
  const photos = photosQuery.data?.photos ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Photos stay with this NCR. They do not show on the customer form.</p>
      {readOnly ? null : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              event.target.value = "";
              if (files.length > 0) upload.mutate(files);
            }}
          />
          <Button type="button" variant="secondary" disabled={upload.isPending} onClick={() => fileInputRef.current?.click()}>
            {upload.isPending ? "Uploading" : "Upload photos"}
          </Button>
        </>
      )}
      {photosQuery.isError ? <p className="text-sm text-red-700">Could not load photos.</p> : null}
      {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
      {photos.length === 0 ? (
        <p className="text-sm text-slate-600">No photos yet.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {photos.map((photo) => (
            <li key={photo.id} className="rounded-md border border-slate-200 bg-white p-2">
              <NcrPhotoPreview ncrId={ncrId} photo={photo} />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="truncate text-xs text-slate-600">{photo.filename}</span>
                {readOnly ? null : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(photo.id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NcrPhotoPreview({ ncrId, photo }: { ncrId: number; photo: QualityNcrDevicePhoto }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchQualityNcrPhotoBlob(ncrId, photo.id)
      .then(({ blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl != null) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.id, ncrId]);

  if (url == null) {
    return <div className="flex h-36 items-center justify-center rounded bg-slate-100 text-xs text-slate-500">Loading photo</div>;
  }
  return <img src={url} alt={photo.filename} className="h-36 w-full rounded object-contain bg-slate-100" />;
}
